import React, { useState } from "react";
import {
  Text,
  Box,
  Modal as ModalMantine,
  Button,
  useMantineTheme,
} from "@mantine/core";
import { Key } from "iconsax-react";
import { FormProvider, useForm } from "react-hook-form";
import { Flex } from "@mantine/core";
import { Modal, TextInputControlled } from "../../../../../components";
import { validationConnectOkx } from "../../../../../utils/validation/schemas/validationConnectOkx";
import { yupResolver } from "@hookform/resolvers/yup";
import { IAddAccountOkx } from "../../../../../interfaces/OkxAccount";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postAddAccountOkx } from "../../../../../hooks";
import { useTranslation } from "react-i18next";

interface IProps {
  open: boolean;
  setClose: () => void;
  setSuccess: () => void;
}

const ModalOkxConnect: React.FC<IProps> = ({ open, setClose, setSuccess }) => {
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorHelp, setErrorHelp] = useState("");

  const methods = useForm<IAddAccountOkx>({
    resolver: yupResolver(validationConnectOkx),
    defaultValues: {
      apiKey: "",
      secretKey: "",
      passphrase: "",
    },
  });

  const { handleSubmit, reset } = methods;

  const handleCloseSuccess = () => {
    reset();
    setIsSuccess(false);
    setIsFailed(false);
    setSuccess();
  };

  const handleCloseFailed = () => {
    setIsFailed(false);
    setErrorMessage("");
    setErrorHelp("");
  };

  const handleClose = () => {
    reset();
    setIsSuccess(false);
    setIsFailed(false);
    setClose();
  };

  const { mutate } = useMutation(postAddAccountOkx, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["MyPlans"], exact: false });
      setIsSuccess(true);
      setIsLoading(false);
    },
    onError: (error: any) => {
      const apiErrorMessage = error?.response?.data?.message;
      const apiErrorHelp = error?.response?.data?.help;
      setErrorMessage(apiErrorMessage || "");
      setErrorHelp(apiErrorHelp || "");
      setIsFailed(true);
      setIsLoading(false);
    },
  });

  const onSubmit = (data: IAddAccountOkx) => {
    setIsLoading(true);
    try {
      mutate(data);
    } catch (error) {
      setIsFailed(true);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        type={"success"}
        titleFirstButton={""}
        titleSeccondButton={t("global.modal.close")}
        description={t(
          "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.success_message"
        )}
        open={isSuccess}
        setClose={handleCloseSuccess}
        secondClick={handleCloseSuccess}
        firstClick={() => {}}
      />
      <Modal
        type={"danger"}
        titleFirstButton={""}
        titleSeccondButton={t("global.modal.close")}
        description={
          errorMessage
            ? `${errorMessage}. ${errorHelp || t("meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.error_hint")}`
            : t("meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.error_message")
        }
        open={isFailed}
        setClose={handleCloseFailed}
        secondClick={handleCloseFailed}
        firstClick={() => {}}
      />
      <ModalMantine
        size="auto"
        opened={open}
        onClose={handleClose}
        closeOnClickOutside={false}
        transitionProps={{
          exitDuration: 600,
          transition: "slide-up",
          duration: 600,
          timingFunction: "ease",
        }}
        centered
        styles={{
          content: {
            background: theme.colors.primaryOpacity[1],
            borderRadius: "0.75rem",
            minWidth: "min(28rem, 95vw)",
          },
          inner: { zIndex: 1002 },
          overlay: { zIndex: 1001 },
          header: {
            marginBottom: "0",
            background: "transparent",
            height: "4rem",
          },
          close: {
            display: "none",
            position: "absolute",
            top: "0.75rem",
            right: "0.75rem",
            svg: {
              height: "1.5rem",
              width: "1.5rem",
            },
          },
          body: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: "3rem 3rem 0.75rem 0.75rem",
            padding: "0 2rem 2rem 2rem",
          },
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }} style={{ width: "100%" }}>
            <Flex direction={"column"} align={"center"} w="100%">
              <Box mt="-1.75rem" mb="0.5rem">
                <Key
                  size="48"
                  color={theme.colors.primary[1]}
                  variant="Bold"
                />
              </Box>
              <Flex direction="column" w="100%" gap="1rem" align={"center"}>
                <Text fw={600} fz="1.375rem" lh="1.75rem" ta="center">
                  {t(
                    "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.title"
                  )}
                </Text>
                <Text
                  c="basic.4"
                  fz="0.875rem"
                  lh="1.25rem"
                  ta={"center"}
                  fw={400}
                >
                  {t(
                    "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.description"
                  )}
                </Text>
                <Flex
                  w="100%"
                  gap="1rem"
                  direction="column"
                >
                  <TextInputControlled
                    name="apiKey"
                    label={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.api_key_label"
                    )}
                    description={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.api_key_description"
                    )}
                    withAsterisk
                    placeholder={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.api_key_placeholder"
                    )}
                    w={"100%"}
                  />
                  <TextInputControlled
                    name="secretKey"
                    label={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.secret_key_label"
                    )}
                    description={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.secret_key_description"
                    )}
                    withAsterisk
                    placeholder={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.secret_key_placeholder"
                    )}
                    w={"100%"}
                  />
                  <TextInputControlled
                    name="passphrase"
                    label={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.passphrase_label"
                    )}
                    description={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.passphrase_description"
                    )}
                    withAsterisk
                    placeholder={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.passphrase_placeholder"
                    )}
                    w={"100%"}
                  />
                </Flex>
                <Flex
                  w="100%"
                  gap="1rem"
                  direction={{ base: "column", 991: "row" }}
                >
                  <Button
                    type="button"
                    variant="primaryOutline"
                    fullWidth
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    {t("global.modal.cancel")}
                  </Button>
                  <Button
                    variant={"primary"}
                    fullWidth
                    type="submit"
                    loading={isLoading}
                  >
                    {isLoading
                      ? t("meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.loading_button")
                      : t("meu_portfolio.minhas_compras.configuracao_do_serviço.modal_okx.connect_button")
                    }
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </ModalMantine>
    </>
  );
};

export default ModalOkxConnect;
